import { Phone, Mail, Globe, Calendar } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { AGENT, CONSULT_TYPES, IMAGES } from "@/data";
import { LeadForm } from "@/components/LeadForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  ["How quickly will Richard respond?", "Richard personally follows up on every inquiry, typically within one business day."],
  ["Is there any cost to consult?", "No — consultations and discovery calls are complimentary and no-pressure."],
  ["Can we meet virtually?", "Yes. In-person and virtual (video) consultations are both available."],
  ["Do you help outside these four counties?", "Richard focuses on Fulton, Gwinnett, DeKalb, and Walton, but reach out to discuss your needs."],
];

export default function Contact() {
  return (
    <div className="bg-cream pt-24">
      <section className="relative px-6 md:px-12 py-16 overflow-hidden">
        <img src={IMAGES.skyline2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative max-w-7xl mx-auto">
          <Reveal><h1 className="font-serif text-5xl md:text-6xl text-navy tracking-tighter mb-3">Talk with Richard</h1></Reveal>
          <Reveal delay={0.1}><p className="text-lg text-charcoal/75 max-w-xl">Reach out with any question, or request a consultation. No pressure, ever.</p></Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-8 border border-black/5">
            <LeadForm type="consultation" source="contact-page" title="Send a Message / Request a Consultation"
              successMsg="Thank you! Richard will be in touch shortly."
              extraFields={[
                { name: "consult_type", label: "What are you interested in?", type: "select", options: CONSULT_TYPES },
                { name: "message", label: "Message", type: "textarea", placeholder: "Tell Richard how he can help…" },
              ]} />
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-navy text-cream rounded-3xl p-8">
            <img src={AGENT.headshot} alt={AGENT.name} className="w-24 h-24 rounded-2xl object-cover mb-5" />
            <h2 className="font-serif text-2xl mb-1">{AGENT.name}</h2>
            <p className="text-cream/60 text-sm mb-6 font-mono">{AGENT.brokerage} · License #{AGENT.license}</p>
            <div className="space-y-3">
              <a href={AGENT.phoneHref} data-testid="contact-call" className="flex items-center gap-3 hover:text-gold transition-colors"><Phone size={18} /> {AGENT.phone}</a>
              <a href={`mailto:${AGENT.email}`} data-testid="contact-email" className="flex items-center gap-3 hover:text-gold transition-colors break-all"><Mail size={18} /> {AGENT.email}</a>
              <a href={AGENT.website} target="_blank" rel="noreferrer" data-testid="contact-web" className="flex items-center gap-3 hover:text-gold transition-colors"><Globe size={18} /> richardprice.georgiamls.com</a>
            </div>
          </div>
          <div className="bg-white rounded-3xl p-8 border border-black/5">
            <div className="flex items-center gap-2 font-serif text-xl text-navy mb-4"><Calendar size={20} className="text-gold" /> Consultation scheduling</div>
            <p className="text-sm text-charcoal/60 mb-4">Placeholder availability — a Calendly or CRM calendar can be connected later.</p>
            <div className="grid grid-cols-2 gap-2">
              {["Mon–Fri 9a–6p", "Sat 10a–3p", "Virtual & in-person", "Same-week openings"].map((t) => (
                <div key={t} className="bg-cream rounded-xl px-4 py-3 text-sm text-charcoal/70 border border-black/5">{t}</div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-3xl p-8 border border-black/5">
            <div className="font-serif text-xl text-navy mb-2">Service-area map</div>
            <div className="rounded-2xl border border-black/10 h-40 bg-navy/5 flex items-center justify-center text-charcoal/40 font-mono text-sm" data-testid="service-map">Map placeholder — Fulton · Gwinnett · DeKalb · Walton</div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 max-w-3xl mx-auto">
        <h2 className="font-serif text-3xl text-navy mb-6">Frequently asked questions</h2>
        <Accordion type="single" collapsible>
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`c${i}`}>
              <AccordionTrigger className="text-left font-medium text-navy" data-testid={`contact-faq-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-charcoal/70">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
